import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { Icon } from "@iconify/react";
import { Button, Card, List, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

import ContractActionSelect from "js/components/Contracts/ContractsTable/ContractActionSelect";
import { ContractsTableProps } from "js/components/Contracts/types";
import { Contract } from "js/components/types";

import "styles/components/Dashboard/ContractsTable.scss";
import "styles/components/Dashboard/TableDashboard.scss";

import { useAppSelector } from "../../../../redux/hooks";

const ContractsTable: React.FC<ContractsTableProps> = ({
  data,
  pagination,
  setPagination,
  loading,
  screen,
}) => {
  const navigate = useNavigate();
  const { userData } = useAppSelector((state) => state.entities.user);
  const renderPropertyAddress = (property: any) => {
    return property?.address
      ? `${property.address.street || ""} #${property.address.exterior_number || ""}`.trim() ||
          "-"
      : "-";
  };

  const getContractStatus = (status: string) => {
    switch (status) {
      case "signed":
        return "Firmado";
      case "cancelled":
        return "Cancelado";
      case "created":
        return "Pendiente";
      case "signature_in_progress":
        return "En proceso de firma";
      case "payment_in_progress":
        return "En proceso de pago";
      default:
        return "Desconocido";
    }
  };

  const columns: ColumnsType<Contract> = [
    {
      title: "FOLIO",
      dataIndex: "metadata",
      render: (metadata) => metadata?.folio_number ?? "-",
      width: "8%",
      ellipsis: true,
    },
    {
      title: "PROPIETARIO",
      dataIndex: "landlords",
      render: (_: any, contract: Contract) => {
        const landlord = contract.landlords?.[0];
        const landlordName = landlord
          ? `${landlord.name || ""} ${landlord.first_surname || ""}`.trim()
          : "-";

        const handleClick = () => {
          if (contract.id) {
            navigate(`/contracts/${contract.id}/details`);
          }
        };

        return landlordName !== "-" ? (
          <span
            onClick={handleClick}
            className="sentry-mask"
            style={{
              fontWeight: 600,
              cursor: "pointer",
              color: "#000",
              transition: "color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#105ef8";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "#000";
            }}
          >
            {landlordName}
          </span>
        ) : (
          <span>{landlordName}</span>
        );
      },
      width: "18%",
      ellipsis: true,
    },
    {
      title: "FECHA DE INICIO",
      dataIndex: "start_date",
      // Long-form Spanish, matches TenantsTable / PropertiesTable style
      // ("2 de julio de 2026"). Parses through dayjs first so any of the
      // backend's date formats (ISO, DD/MM/YYYY, MM/DD/YYYY) is accepted
      // unambiguously before handing off to Intl for the display.
      render: (start_date) => {
        if (!start_date) return "-";
        const date = dayjs(
          start_date,
          ["DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY"],
          true,
        );
        return date.isValid()
          ? date.toDate().toLocaleDateString("es-ES", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "-";
      },
      width: "12%",
      align: "center" as const,
    },
    {
      title: "FECHA DE TÉRMINO",
      dataIndex: "end_date",
      render: (end_date) => {
        if (!end_date) return "-";
        const date = dayjs(
          end_date,
          ["DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY"],
          true,
        );
        return date.isValid()
          ? date.toDate().toLocaleDateString("es-ES", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "-";
      },
      width: "12%",
      align: "center" as const,
    },
    {
      title: "ESTADO",
      dataIndex: "status",
      render: (status) => getContractStatus(status),
      width: "12%",
      align: "center" as const,
    },
    {
      title: "FECHA DE FIRMA",
      dataIndex: "closed_at",
      render: (closed_at) => {
        if (!closed_at) return "-";
        const date = dayjs(
          closed_at,
          ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY"],
          true,
        );
        return date.isValid() ? date.format("DD/MM/YYYY") : "-";
      },
      width: "13%",
      align: "center" as const,
    },
    {
      title: "FECHA DE CREACIÓN",
      dataIndex: "created_at",
      // Matches the long-form Spanish date used by the other tables in the
      // app (e.g. TenantsTable, PropertiesTable) — "2 de julio de 2026".
      render: (created_at) => {
        if (!created_at) return "-";
        return new Date(created_at).toLocaleDateString("es-ES", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      },
      width: "12%",
      align: "center" as const,
    },
    {
      title: "ACCIONES",
      dataIndex: "actions",
      align: "center" as const,
      render: (actions, contract) => (
        <ContractActionSelect contract={contract} />
      ),
      width: "13%",
    },
  ];

  const headerCell = (props: any) => (
    <th
      {...props}
      style={{
        textAlign:
          props.children.includes("ACCIONES") ||
          props.children.includes("FECHA DE CREACIÓN") ||
          props.children.includes("FECHA DE INICIO") ||
          props.children.includes("FECHA DE TÉRMINO") ||
          props.children.includes("ESTADO") ||
          props.children.includes("FECHA DE FIRMA")
            ? "center"
            : undefined,
      }}
    />
  );

  return (
    <>
      {!screen?.md ? (
        <>
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 2,
              md: 4,
              lg: 4,
              xl: 4,
              xxl: 3,
            }}
            dataSource={data}
            renderItem={(item) => {
              console.log("Rendering item, is_staff:", userData?.is_staff);
              return (
                <List.Item key={item.id}>
                  <Card
                    title={`Folio: ${item.metadata?.folio_number || "-"}`}
                    actions={[
                      ...(userData?.is_staff
                        ? [
                            <Tooltip title="Editar contrato" key="edit">
                              <Button
                                size={"small"}
                                type={"link"}
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  navigate(`/contracts/${item.id}/edit`);
                                }}
                              >
                                <Icon fontSize={20} icon="akar-icons:edit" />
                              </Button>
                            </Tooltip>,
                          ]
                        : []),
                      <Tooltip title="Ver detalles" key="view">
                        <Button
                          size={"small"}
                          type={"link"}
                          onClick={(e) => {
                            e?.stopPropagation();
                            navigate(`/contracts/${item.id}/details`);
                          }}
                        >
                          <Icon fontSize={20} icon="lsicon:view-outline" />
                        </Button>
                      </Tooltip>,
                    ]}
                  >
                    <span style={{ fontWeight: "bold" }}>ID Contrato: </span>
                    <span>{item.document_id || "-"}</span>
                    <br />
                    <span style={{ fontWeight: "bold" }}>Dirección: </span>
                    <span className="sentry-mask">{renderPropertyAddress(item.property)}</span>
                    <br />
                    <span style={{ fontWeight: "bold" }}>Propietario: </span>
                    <span className="sentry-mask">
                      {item.landlords?.[0]
                        ? `${item.landlords[0].name || ""} ${item.landlords[0].first_surname || ""}`.trim()
                        : "-"}
                    </span>
                    <br />
                    <span style={{ fontWeight: "bold" }}>
                      Fecha de inicio:{" "}
                    </span>
                    <span>
                      {item.start_date
                        ? dayjs(item.start_date).format("DD/MM/YYYY")
                        : "-"}
                    </span>
                    <br />
                    <span style={{ fontWeight: "bold" }}>
                      Fecha de término:{" "}
                    </span>
                    <span>
                      {item.end_date
                        ? dayjs(item.end_date).format("DD/MM/YYYY")
                        : "-"}
                    </span>
                    <br />
                    <span style={{ fontWeight: "bold" }}>
                      Estatus de firma:{" "}
                    </span>
                    <span>
                      {item.status === "signed" ? "Firmado" : "Pendiente"}
                    </span>
                    <br />
                    <span style={{ fontWeight: "bold" }}>Fecha de firma: </span>
                    <span>
                      {item.closed_at
                        ? dayjs(
                            item.closed_at,
                            ["DD/MM/YYYY HH:mm:ss"],
                            true,
                          ).isValid()
                          ? dayjs(item.closed_at, [
                              "DD/MM/YYYY HH:mm:ss",
                            ]).format("DD/MM/YYYY")
                          : "-"
                        : "-"}
                    </span>
                  </Card>
                </List.Item>
              );
            }}
          />
        </>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table
            className="contracts-table"
            size={"small"}
            columns={columns}
            rowKey={(record) => record.id ?? ""}
            dataSource={data}
            pagination={pagination}
            loading={loading}
            onChange={(paginationConfig) => {
              setPagination({
                current: paginationConfig.current || 1,
                pageSize: paginationConfig.pageSize || 10,
                total: paginationConfig.total || 0,
              });
            }}
            scroll={{ x: "max-content" }}
            components={{
              header: { cell: headerCell },
            }}
          />
        </div>
      )}
    </>
  );
};

export default ContractsTable;
